import {
    computed,
    onMounted,
    onUnmounted,
    ref,
} from 'vue';

import { errorGuard } from '@utils/ErrorGuard';
import { useHelpStore } from '@src/store/external/useHelpStore';
import { useAuthenticationStore } from '@src/store/external/useAuthenticationStore';

export const ResendEmailState = {
    Available: 'Available',
    Timeout: 'Timeout',
    Sent: 'Sent',
    Error: 'Error',
} as const;

export type ResendEmailState = typeof ResendEmailState[keyof typeof ResendEmailState];

const emailSentTimeKey = 'lastEmailSentTime';

export function useResendEmail(isPersistent = false, minutesTimeout = 2, hasDefaultTimeout = false) {
    const timerTimeout = ref<NodeJS.Timeout | null>(null);
    const successTimeout = ref<NodeJS.Timeout | null>(null);
    const hasError = ref(false);
    const timeoutMs = minutesTimeout * 60 * 1000;

    const { sendActivationEmail: resendActivationEmailAction } = useAuthenticationStore();

    const { showHelp } = useHelpStore();

    const isLoading = ref(false);

    const asyncError = ref('');

    async function resendActivationEmail(email: string, shouldShowHelpOnError = false) {
        await errorGuard(async () => {
            hasError.value = false;
            asyncError.value = '';
            isLoading.value = true;

            await resendActivationEmailAction({ email });

            setLastEmailSentTime();
            startTimeoutTimer();
            setSuccessTimer();
        }, {
            onError: (e) => {
                asyncError.value = e.message;
                hasError.value = true;

                if (shouldShowHelpOnError) {
                    showHelp({});
                }
            },
        });

        isLoading.value = false;
    }

    onMounted(() => {
        if (isPersistent) {
            checkLastEmailSentTime();
        } else {
            startTimeoutTimer(timeoutMs);
        }
    });

    function startTimeoutTimer(period = timeoutMs): void {
        clearTimeoutTimer();
        timerTimeout.value = setTimeout(() => {
            clearTimeoutTimer();
        }, period);
    }

    function clearTimeoutTimer(): void {
        if (timerTimeout.value) {
            clearTimeout(timerTimeout.value);
            timerTimeout.value = null;
        }
    }

    function setSuccessTimer(): void {
        successTimeout.value = setTimeout(() => {
            if (successTimeout.value) {
                clearTimeout(successTimeout.value);
                successTimeout.value = null;
            }
        }, 10 * 1000);
    }

    function getLastEmailSentTime(): number {
        return parseInt(localStorage.getItem(emailSentTimeKey) || '') || 0;
    }

    function hasLastEmailSentTime(): boolean {
        return localStorage.getItem(emailSentTimeKey) !== null;
    }

    function setLastEmailSentTime(): void {
        if (!isPersistent) {
            return;
        }

        localStorage.setItem(emailSentTimeKey, Date.now().toString());
    }

    function checkLastEmailSentTime(): void {
        const timeRemaining = timeoutMs - (Date.now() - getLastEmailSentTime());

        if (timeRemaining > 0) {
            startTimeoutTimer(timeRemaining);
        } else if (!hasLastEmailSentTime() && hasDefaultTimeout) {
            startTimeoutTimer();
        }
    }

    const emailState = computed(() => {
        if (hasError.value) {
            return ResendEmailState.Error;
        }

        if (successTimeout.value) {
            return ResendEmailState.Sent;
        }

        if (timerTimeout.value) {
            return ResendEmailState.Timeout;
        }

        return ResendEmailState.Available;
    });

    onUnmounted(() => {
        clearTimeoutTimer();
    });

    return {
        isLoading,
        asyncError,
        emailState,
        resendActivationEmail,
    };
}
